import clsx from "clsx";
import Button from "components/Button";
import useFeatureFlag from "hooks/useFeatureFlag";
import React from "react";
import { useTranslation } from "react-i18next";
import { RELEASE_NOTES_URL, skeleton } from "utils";

const URL = "https://retool.prod.concular.com/url/circularlca-release-notes";

interface ReleaseNote {
  Release: { number: number };
  Version: { id: string; select: { name: string } };
  Beschreibung: { rich_text: { plain_text: string }[] };
  Tags: { multi_select: { name: string; color: string }[] };
  Name: { title: { plain_text: string }[] };
  "Release Date German": { formula: { string: string } };
}

const ReleaseNotes = () => {
  const { t } = useTranslation();
  const [data, setData] = React.useState<ReleaseNote[] | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<unknown>(null);
  const { isFeatureVisible } = useFeatureFlag();

  const getNotes = async () => {
    try {
      const response = await fetch(URL);
      if (!response.ok) {
        throw new Error("Fetch Relase Notes: Network response was not ok");
      }
      const data = await response.json();
      setData(data);
    } catch (error: unknown) {
      setError(error);
    } finally {
      setLoading(false);
    }
  };

  function getColor(tag: { name: string; color: string }) {
    if (tag.color === "default") {
      return "bg-gray-100";
    } else if (tag.color === "orange") {
      return "bg-orange-100";
    }
    return `bg-${tag.color}-100`;
  }

  React.useEffect(() => {
    isFeatureVisible("release_notes") && getNotes();
  }, []);

  if (error || !isFeatureVisible("release_notes")) return null;
  if (loading) return skeleton("h-[460px]");

  return (
    <div className="border mb-6 border-gray-300 bg-white rounded-lg overflow-hidden flex-1 flex flex-col justify-between">
      <div>
        <div className="text-base text-gray-900 font-semibold pt-4 p-4">
          {t("dashboard.releaseNotes")}
        </div>
        <ul className="max-h-[555px] min-h-[555px] overflow-y-auto w-full px-4 pb-4">
          {data?.map((note, index) => (
            <li
              key={index}
              className={clsx(
                "border-b border-gray-200 py-2 first-of-type:pt-0 last-of-type:pb-0 last-of-type:border-b-0 w-full"
              )}
            >
              <span className="text-sm bg-blue-100 py-0.5 rounded-sm px-1 text-gray-800">
                {note["Release Date German"].formula?.string ?? "-"}
              </span>
              <div className="font-semibold text-sm my-1">
                {note.Name?.title?.[0]?.plain_text}
              </div>
              <div className="flex mb-1">
                {note.Tags.multi_select.map((tag, index) => (
                  <span
                    key={index}
                    className={clsx(
                      getColor(tag),
                      "text-gray-800 px-1 rounded-sm mr-1 text-sm"
                    )}
                  >
                    {tag.name}
                  </span>
                ))}
              </div>
              <div className="text-sm my-1">
                {note.Beschreibung?.rich_text?.[0]?.plain_text}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className="bg-gray-50 px-4 py-3 flex justify-end">
        <Button
          width="fit-content"
          type="secondary"
          onClick={() => window.open(RELEASE_NOTES_URL, "_blank")}
        >
          {t("dashboard.otherReleaseNotes")}
        </Button>
      </div>
    </div>
  );
};

export default ReleaseNotes;
